import { COLLISION_CELLS_PER_TILE, type MapDocument } from "../document/types.ts";
import type { FoldedZones } from "../document/zones.ts";
import type { CompileContext } from "./context.ts";

/**
 * Zones in the server chunk (ADR-054's amendment of 2026-09-27, decision 6): the check that every painted type
 * is one the game registers, and one chunk's cells of the map's folded zone grid with the chunk's own table.
 */

/**
 * One server chunk's zones: `(chunkSize * COLLISION_CELLS_PER_TILE)^2` bytes, each 0 or a 1-based index into
 * `types`, the registered ids in the order the chunk first meets them.
 */
export interface ChunkZones {
  readonly types: readonly string[];
  readonly cells: Uint8Array;
}

/**
 * Refuses a zone layer painting a type the game does not register. A layer is canonical, so its table lists
 * exactly the types its cells paint.
 *
 * @param map The map.
 * @param context The compile context, whose `zoneTypes` are the registered ids.
 * @throws Error naming the map, the layer and the type.
 */
export function checkZoneTypes(map: MapDocument, context: CompileContext): void {
  const registered = new Set(context.zoneTypes ?? []);
  for (const layer of map.layers) {
    if (layer.kind !== "zone") {
      continue;
    }
    for (let i = 0; i < layer.cells.length; i += 1) {
      const value = layer.cells[i] as number;
      const type = value === 0 ? undefined : layer.types[value - 1];
      if (type !== undefined && !registered.has(type)) {
        throw new Error(`map ${map.id}, zone layer ${layer.id} paints type ${type}, which the game does not register`);
      }
    }
  }
}

/**
 * One chunk's cells of a map's folded zones, renumbered into the chunk's own table.
 *
 * @param folded The map's folded zones.
 * @param map The map, for its size.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @param chunkSize Chunk size in tiles.
 * @returns The chunk's zones, or null when it holds no painted cell.
 */
export function chunkZones(folded: FoldedZones, map: MapDocument, cx: number, cy: number, chunkSize: number): ChunkZones | null {
  if (folded.types.length === 0) {
    return null;
  }
  const side = chunkSize * COLLISION_CELLS_PER_TILE;
  const gridWidth = map.width * COLLISION_CELLS_PER_TILE;
  const gridHeight = map.height * COLLISION_CELLS_PER_TILE;
  const cells = new Uint8Array(side * side);
  const remap = new Uint8Array(folded.types.length + 1);
  const types: string[] = [];
  for (let y = 0; y < side; y += 1) {
    const gridY = cy * side + y;
    if (gridY >= gridHeight) {
      break;
    }
    for (let x = 0; x < side; x += 1) {
      const gridX = cx * side + x;
      if (gridX >= gridWidth) {
        break;
      }
      const value = folded.cells[gridY * gridWidth + gridX] as number;
      if (value === 0) {
        continue;
      }
      if (remap[value] === 0) {
        types.push(folded.types[value - 1] as string);
        remap[value] = types.length;
      }
      cells[y * side + x] = remap[value] as number;
    }
  }
  return types.length === 0 ? null : { types, cells };
}
